import { ReactNode, useCallback } from 'react';

import { GroupBase, OptionsOrGroups, LoadOptions } from '@any-ui-react/select';
import { OperationVariables } from '@apollo/client';
import type { useLazyQuery, useQuery } from '@apollo/client/react';

import { InputMaybe, Scalars } from '@anyx/common-graphql';
import { DEFAULT_OPTION_SIZE } from '@anyx/shared-utils';


export type LoadOptionAdditional = {
  pageNumber: number;
};

export interface LoadOptionDefaultOption<T> {
  label: ReactNode;
  value: T;
}

interface RequiredResponse<I> {
  [key: string]: {
    total: number;
    items: readonly I[];
  };
}

interface RequiredVariables {
  pageNumber: number;
  pageSize: number;
  filter?: InputMaybe<{
    readonly keyword?: InputMaybe<Scalars['String']['input']>;
    [key: string]: unknown;
  }>;
}

interface LoadSelectOptionsProps<I, V, LQ, LV extends OperationVariables> {
  labelFormatter: (item: I) => ReactNode;
  valueFormatter: (item: I) => V;
  optionMapper?: (item: I) => LoadOptionDefaultOption<V>;
  listQuery: {
    lazyQuery: useLazyQuery.ExecFunction<LQ, LV>;
    options?: useQuery.Options<LQ, LV>;
    variables?: Omit<LV, 'pageNumber' | 'pageSize'>;
    scope: keyof LQ;
    hasMoreChecker?: (
      loadedOptions: LoadOptionDefaultOption<V>[],
      options: OptionsOrGroups<LoadOptionDefaultOption<V>, GroupBase<LoadOptionDefaultOption<V>>>,
      data?: LQ
    ) => boolean;
  };
}

export const useLoadOptions = <
  I, // Type used in formatter Functions,
  V, // Type used has select value
  LQ extends RequiredResponse<I>, // Type of List Query
  LV extends RequiredVariables, // Variables of List Query
>({
  labelFormatter,
  valueFormatter,
  optionMapper = (item) => ({
    label: labelFormatter(item),
    value: valueFormatter(item),
  }),
  listQuery: {
    options: listOptions,
    variables: listVariables,
    scope: listScope,
    lazyQuery: listQuery,
    hasMoreChecker = (loadedOptions, newOptions, data) => {
      return loadedOptions.length + newOptions.length < (data?.[listScope]?.total || 0);
    },
  },
}: LoadSelectOptionsProps<I, V, LQ, LV>) => {
  const loadOptions: LoadOptions<
    LoadOptionDefaultOption<V>,
    GroupBase<LoadOptionDefaultOption<V>>,
    LoadOptionAdditional
  > = useCallback(
    async (keyword, loadedOptions = [], additional = { pageNumber: 1 }) => {
      try {
        const requiredVariables: Pick<LV, 'pageNumber' | 'pageSize' | 'filter'> = {
          pageNumber: additional.pageNumber,
          pageSize: DEFAULT_OPTION_SIZE,
          filter: { ...(keyword ? { keyword } : {}) },
        };

        const { data } = await listQuery({
          ...listOptions,
          variables: {
            ...listVariables,
            ...requiredVariables,
            filter: {
              ...requiredVariables?.filter,
              ...listVariables?.filter,
            },
          } as LV,
        });

        const newItems = data?.[listScope]?.items || [];

        const mappedOptions = newItems.map((item) => optionMapper(item));
        const hasMore = hasMoreChecker(mappedOptions, loadedOptions, data);
        return {
          options: !additional.pageNumber ? [...mappedOptions] : mappedOptions,
          hasMore,
          additional: { pageNumber: requiredVariables.pageNumber + 1 },
        };
      } catch (_) {
        return {
          options: [],
          hasMore: false,
          additional,
        };
      }
    },
    [hasMoreChecker, listOptions, listQuery, listScope, listVariables, optionMapper]
  );

  return { loadOptions };
};
